import { cn } from '../../utils/cn';
import { actionMeta, outcomeMeta } from '../../utils/logbook';

interface ActionBadgeProps {
  action: string;
  /** Hides the label, leaving the tinted icon for dense rows. */
  iconOnly?: boolean;
  className?: string;
}

/**
 * An action as a badge, coloured by what it does rather than by where it came from: a removal reads
 * red whether it was a pickaxe, an ME terminal or a Carry On grab, so a page of mixed integrations
 * still scans in one pass.
 */
export const LogbookActionBadge = ({ action, iconOnly = false, className }: ActionBadgeProps) => {
  const meta = actionMeta(action);
  const Icon = meta.icon;
  const title = `${meta.label} (${action}) via ${meta.source}`;

  if (iconOnly) {
    return (
      <span className={cn('icon-tile icon-tile-sm', `icon-tile-${meta.tone}`, className)} title={title}>
        <Icon className="w-3.5 h-3.5" />
      </span>
    );
  }
  return (
    <span className={cn('badge', `badge-${meta.tone}`, 'gap-1', className)} title={title}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

interface OutcomeBadgeProps {
  outcome: string;
  className?: string;
}

/**
 * The outcome badge is load-bearing. `observed` and `attempted` are the difference between evidence
 * and an accusation, so they are never dropped to save space.
 */
export const LogbookOutcomeBadge = ({ outcome, className }: OutcomeBadgeProps) => {
  const meta = outcomeMeta(outcome);
  return (
    <span className={cn('badge', `badge-${meta.tone}`, className)} title={meta.meaning}>
      {meta.label}
    </span>
  );
};
